import type { RichTableBlockModel } from '@blocksuite/affine-model';
import type { Store } from '@blocksuite/store';

import { DEFAULT_COLUMN_WIDTH } from '../styles.js';
import {
  buildRichTableRows,
  MAX_COLUMNS,
  MAX_ROWS,
} from './insert-rich-table.js';
import {
  normalizeRichTable,
  rowCells,
  tableRows,
} from './normalize-rich-table.js';

/** Inserts an empty row at `index` (default: append). */
export function insertRow(
  store: Store,
  table: RichTableBlockModel,
  index?: number
) {
  if (store.readonly) return;
  const rows = tableRows(table);
  if (rows.length >= MAX_ROWS) return;
  buildRichTableRows(
    store,
    table.id,
    table.props.columnWidths.length,
    1,
    index
  );
}

/** Deletes the row at `index`; the last remaining row takes the table with it. */
export function deleteRow(
  store: Store,
  table: RichTableBlockModel,
  index: number
) {
  if (store.readonly) return;
  const row = tableRows(table)[index];
  if (!row) return;
  store.deleteBlock(row);
  normalizeRichTable(store, table);
}

/**
 * Inserts an empty column at `index` (default: append), giving it the average
 * width of the existing columns so the table does not visibly jump.
 */
export function insertColumn(
  store: Store,
  table: RichTableBlockModel,
  index?: number
) {
  if (store.readonly) return;

  const widths = [...table.props.columnWidths];
  if (widths.length >= MAX_COLUMNS) return;

  const at = index ?? widths.length;

  store.transact(() => {
    widths.splice(at, 0, DEFAULT_COLUMN_WIDTH);
    table.props.columnWidths = widths;
  });

  for (const row of tableRows(table)) {
    const cellId = store.addBlock('affine:rich-table-cell', {}, row.id, at);
    store.addBlock('affine:paragraph', {}, cellId);
  }
}

/** Deletes the column at `index`; the last remaining column removes the table. */
export function deleteColumn(
  store: Store,
  table: RichTableBlockModel,
  index: number
) {
  if (store.readonly) return;

  const widths = [...table.props.columnWidths];
  if (index < 0 || index >= widths.length) return;

  for (const row of tableRows(table)) {
    const cell = rowCells(row)[index];
    if (cell) store.deleteBlock(cell);
  }

  store.transact(() => {
    widths.splice(index, 1);
    table.props.columnWidths = widths;
  });

  normalizeRichTable(store, table);
}

/**
 * Moves a column to `to`, taking one cell per row with it.
 *
 * `to` is the index the column should end up at, read against the *original*
 * ordering — the same convention `Array#splice` follows after a removal.
 */
export function moveColumn(
  store: Store,
  table: RichTableBlockModel,
  from: number,
  to: number
) {
  if (store.readonly) return;

  const widths = [...table.props.columnWidths];
  if (from < 0 || from >= widths.length) return;
  const target = Math.min(Math.max(to, 0), widths.length - 1);
  if (target === from) return;

  for (const row of tableRows(table)) {
    const cells = rowCells(row);
    const moving = cells[from];
    const anchor = cells[target];
    if (!moving || !anchor) continue;
    // Moving right lands after the anchor, moving left before it.
    store.moveBlocks([moving], row, anchor, target < from);
  }

  store.transact(() => {
    const [width] = widths.splice(from, 1);
    widths.splice(target, 0, width ?? DEFAULT_COLUMN_WIDTH);
    table.props.columnWidths = widths;
  });
}

/** Moves a row to `to`, read against the original ordering. */
export function moveRow(
  store: Store,
  table: RichTableBlockModel,
  from: number,
  to: number
) {
  if (store.readonly) return;

  const rows = tableRows(table);
  if (from < 0 || from >= rows.length) return;
  const target = Math.min(Math.max(to, 0), rows.length - 1);
  if (target === from) return;

  const moving = rows[from];
  const anchor = rows[target];
  if (!moving || !anchor) return;
  store.moveBlocks([moving], table, anchor, target < from);
}
